import axiosInstance from "./AxiosInstance";

const WHATSAPP_BASE_URL = "/api/v3/notificaciones/whatsapp";

class WhatsAppService {
  // Create a WhatsApp instance with optional phone number
  async createInstance(phoneNumber = null) {
    try {
      const payload = {};
      // Only include phone_number if explicitly provided
      if (phoneNumber && phoneNumber.trim() !== "") {
        payload.phone_number = phoneNumber;
      }

      const response = await axiosInstance.post(
        `${WHATSAPP_BASE_URL}/register-phone/`,
        payload,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Register or connect a phone number (legacy method)
  async registerPhone(phoneNumber) {
    try {
      const response = await axiosInstance.post(
        `${WHATSAPP_BASE_URL}/register-phone/`,
        {
          phone_number: phoneNumber,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Get WhatsApp instance status
  async getStatus() {
    try {
      const response = await axiosInstance.get(`${WHATSAPP_BASE_URL}/status/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Get QR code for WhatsApp connection
  async getQRCode() {
    try {
      const response = await axiosInstance.get(`${WHATSAPP_BASE_URL}/qr-code/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Disconnect WhatsApp instance
  async disconnect() {
    try {
      const response = await axiosInstance.post(
        `${WHATSAPP_BASE_URL}/disconnect/`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Start polling WhatsApp status with automatic timeout
   * @param {Object} options - Polling configuration options
   * @param {Function} options.onStatusUpdate - Callback called on each status check with statusData
   * @param {Function} options.onConnected - Callback called when WhatsApp is connected with statusData
   * @param {Function} options.onTimeout - Callback called when polling times out
   * @param {Function} options.onCountdown - Callback called every second with countdown info
   * @param {number} options.timeoutMinutes - Minutes before timeout (default: 3-5)
   * @param {number} options.intervalSeconds - Seconds between status checks (default: 5)
   * @returns {Object} Controller object with stop() method and isPolling() method
   */
  async startStatusPolling({
    onStatusUpdate,
    onConnected,
    onTimeout,
    onCountdown,
    timeoutMinutes = 3,
    intervalSeconds = 5,
  } = {}) {
    let pollingInterval = null;
    let timeoutTimer = null;
    let countdownInterval = null;
    let isPolling = true;
    let startTime = Date.now();
    let totalTimeMs = timeoutMinutes * 60 * 1000;

    const cleanup = () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
        timeoutTimer = null;
      }
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      isPolling = false;
    };

    const pollStatus = async () => {
      if (!isPolling) return;

      try {
        const statusData = await this.getStatus();

        // Call status update callback if provided
        if (onStatusUpdate) {
          onStatusUpdate(statusData);
        }

        // Check if connected
        if (statusData.status === "connected" || statusData.status === "open") {
          cleanup();
          if (onConnected) {
            onConnected(statusData);
          }
          return;
        }
      } catch (error) {
        console.error("Error during WhatsApp status polling:", error);
      }
    };

    // Start polling
    pollingInterval = setInterval(pollStatus, intervalSeconds * 1000);

    // Start countdown if callback provided
    if (onCountdown) {
      countdownInterval = setInterval(() => {
        if (!isPolling) return;

        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, totalTimeMs - elapsed);
        const remainingSeconds = Math.ceil(remaining / 1000);

        onCountdown({
          remainingSeconds,
          remainingMinutes: Math.ceil(remainingSeconds / 60),
          percentageRemaining: Math.max(0, (remaining / totalTimeMs) * 100),
          isExpired: remaining <= 0,
        });
      }, 1000);
    }

    // Set timeout
    timeoutTimer = setTimeout(() => {
      cleanup();
      if (onTimeout) {
        onTimeout();
      }
    }, totalTimeMs);

    // Return cleanup function to allow manual stopping
    return {
      stop: cleanup,
      isPolling: () => isPolling,
    };
  }

  // Check for real-time updates of WhatsApp status
  async checkUpdates() {
    try {
      const response = await axiosInstance.get(
        `${WHATSAPP_BASE_URL}/check-updates/`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Improved polling with real-time updates endpoint
  async startOptimizedStatusPolling({
    onStatusUpdate,
    onConnected,
    onTimeout,
    onQRUpdated,
    onCountdown,
    timeoutMinutes = 3,
    intervalSeconds = 3,
  } = {}) {
    let pollingInterval = null;
    let timeoutTimer = null;
    let countdownInterval = null;
    let isPolling = true;
    let startTime = Date.now();
    let totalTimeMs = timeoutMinutes * 60 * 1000;

    const cleanup = () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
        timeoutTimer = null;
      }
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      isPolling = false;
    };

    const pollStatus = async () => {
      if (!isPolling) return;

      try {
        const updateData = await this.checkUpdates();

        // Call status update callback if provided
        if (onStatusUpdate) {
          onStatusUpdate(updateData);
        }

        // Check for QR updates
        if (onQRUpdated && updateData.qr_available && updateData.qr_code) {
          onQRUpdated(updateData);
        }

        // Check if connected
        if (updateData.is_connected) {
          cleanup();
          if (onConnected) {
            onConnected(updateData);
          }
          return;
        }

        // Check if QR expired and handle cleanup
        if (updateData.qr_expired) {
          cleanup();
          if (onTimeout) {
            onTimeout();
          }
          return;
        }
      } catch (error) {
        console.error("Error during optimized WhatsApp status polling:", error);
      }
    };

    // Start polling - more frequent with optimized endpoint
    pollingInterval = setInterval(pollStatus, intervalSeconds * 1000);

    // Start countdown if callback provided
    if (onCountdown) {
      countdownInterval = setInterval(() => {
        if (!isPolling) return;

        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, totalTimeMs - elapsed);
        const remainingSeconds = Math.ceil(remaining / 1000);

        onCountdown({
          remainingSeconds,
          remainingMinutes: Math.ceil(remainingSeconds / 60),
          percentageRemaining: Math.max(0, (remaining / totalTimeMs) * 100),
          isExpired: remaining <= 0,
        });
      }, 1000);
    }

    // Set timeout
    timeoutTimer = setTimeout(() => {
      cleanup();
      if (onTimeout) {
        onTimeout();
      }
    }, totalTimeMs);

    // Return cleanup function to allow manual stopping
    return {
      stop: cleanup,
      isPolling: () => isPolling,
    };
  }
}

const whatsAppService = new WhatsAppService();
export default whatsAppService;
